/**
 * Password Manager with Expiration Tracking
 * Supports multiple passwords that expire 30 days from first access
 * Also includes token generators for 14 and 21 days
 *
 * TO EDIT PORTFOLIO PASSWORDS: see PASSWORD_CONFIGS in this file (search for "PASSWORD_CONFIGS").
 */

interface PasswordConfig {
  hash: string // Only the hash is stored; never commit plain-text passwords
  expirationDays: number // Days from first access
  description?: string
}

interface PasswordAccess {
  passwordHash: string
  firstAccessedAt: number // Timestamp of first access
  expiresAt: number // Calculated expiration timestamp
}

const STORAGE_KEY = 'portfolio_password_access'
const PASSWORDS_KEY = 'portfolio_passwords_config'

// ═══════════════════════════════════════════════════════════════════════════════
// PORTFOLIO ACCESS PASSWORDS (not admin) — HASHES ONLY, no plain text
// Edit this file: src/utils/passwordManager.ts
// To add a password: run "node scripts/generate-password-hash.js YOUR_PASSWORD"
//   and add the printed hash below. Never commit actual passwords to Git.
// ═══════════════════════════════════════════════════════════════════════════════

const PASSWORD_CONFIGS: PasswordConfig[] = [
  { hash: '3e788f2e', expirationDays: 30, description: '30-day access password 1' },
  { hash: 'e4e553b2', expirationDays: 30, description: '30-day access password 2' },
  { hash: '903426ee', expirationDays: 30, description: '30-day access password 3' }
]

// Simple hash function (same as passwordHash.ts)
function simpleHash(str: string): string {
  let hash = 0
  if (str.length === 0) return hash.toString()
  
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash
  }
  
  const salted = str + 'portfolio_salt_2024'
  let saltedHash = 0
  for (let i = 0; i < salted.length; i++) {
    const char = salted.charCodeAt(i)
    saltedHash = ((saltedHash << 5) - saltedHash) + char
    saltedHash = saltedHash & saltedHash
  }
  
  return Math.abs(hash + saltedHash).toString(16)
}

// Initialize password configs (hashes only; no plain text in repo)
const initializePasswords = (): PasswordConfig[] => {
  return PASSWORD_CONFIGS.map(config => ({ ...config }))
}

class PasswordManager {
  private passwords: PasswordConfig[]
  private accessRecords: Map<string, PasswordAccess> = new Map()

  constructor() {
    this.passwords = initializePasswords()
    this.loadAccessRecords()
  }

  /**
   * Load access records from localStorage
   */
  private loadAccessRecords(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const records = JSON.parse(stored)
        this.accessRecords = new Map(Object.entries(records))
        this.cleanupExpiredRecords()
      }
    } catch (error) {
      // Silently fail - don't expose errors
    }
  }

  /**
   * Save access records to localStorage
   */
  private saveAccessRecords(): void {
    try {
      const records = Object.fromEntries(this.accessRecords)
      localStorage.setItem(STORAGE_KEY, JSON.stringify(records))
    } catch (error) {
      // Silently fail - don't expose errors
    }
  }

  /**
   * Clean up expired access records
   */
  private cleanupExpiredRecords(): void {
    const now = Date.now()
    let hasChanges = false
    
    for (const [hash, access] of this.accessRecords.entries()) {
      if (now > access.expiresAt) {
        this.accessRecords.delete(hash)
        hasChanges = true
      }
    }
    
    if (hasChanges) {
      this.saveAccessRecords()
    }
  }

  /**
   * Verify password and check expiration
   */
  verifyPassword(inputPassword: string): { 
    valid: boolean
    expired: boolean
    expiresAt?: number
    message: string
  } {
    const inputHash = simpleHash(inputPassword)
    
    // Find matching password config
    const passwordConfig = this.passwords.find(p => p.hash === inputHash)
    
    if (!passwordConfig) {
      return {
        valid: false,
        expired: false,
        message: 'Invalid password'
      }
    }

    // Check if we have an access record
    let accessRecord = this.accessRecords.get(inputHash)
    
    if (!accessRecord) {
      // First access - create new record
      const firstAccessedAt = Date.now()
      const expiresAt = firstAccessedAt + (passwordConfig.expirationDays * 24 * 60 * 60 * 1000)
      
      accessRecord = {
        passwordHash: inputHash,
        firstAccessedAt,
        expiresAt
      }
      
      this.accessRecords.set(inputHash, accessRecord)
      this.saveAccessRecords()
    }

    // Check if expired
    if (Date.now() > accessRecord.expiresAt) {
      return {
        valid: true,
        expired: true,
        expiresAt: accessRecord.expiresAt,
        message: 'Password has expired. Please request a new one.'
      }
    }

    // Calculate days remaining
    const daysRemaining = Math.ceil((accessRecord.expiresAt - Date.now()) / (24 * 60 * 60 * 1000))
    
    return {
      valid: true,
      expired: false,
      expiresAt: accessRecord.expiresAt,
      message: `Access granted. ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining.`
    }
  }

  /**
   * Get expiration info for a password
   */
  getExpirationInfo(passwordHash: string): PasswordAccess | null {
    return this.accessRecords.get(passwordHash) || null
  }

  /**
   * List all active passwords with expiration info
   * @private - Not exposed for security
   */
  private listActivePasswords(): Array<{ description: string; expired: boolean; expiresAt?: number }> {
    this.cleanupExpiredRecords()
    
    return this.passwords.map(config => {
      const access = this.accessRecords.get(config.hash)
      const expired = access ? Date.now() > access.expiresAt : false
      // Only return safe information, never passwords or hashes
      return { 
        description: config.description || 'Password', 
        expired,
        expiresAt: access?.expiresAt
      }
    })
  }
}

// Import tempAccessManager at the top level
import { tempAccessManager } from './tempAccess'

// Token Generator for temporary access
export class TokenGenerator {
  /**
   * Generate a token URL for 14 days
   */
  static generate14DayToken(): string {
    return tempAccessManager.generateTempUrl({
      expirationHours: 14 * 24, // 14 days
      maxUses: 100,
      allowedPaths: ['*']
    })
  }

  /**
   * Generate a token URL for 21 days
   */
  static generate21DayToken(): string {
    return tempAccessManager.generateTempUrl({
      expirationHours: 21 * 24, // 21 days
      maxUses: 100,
      allowedPaths: ['*']
    })
  }

  /**
   * Generate a token URL for 30 days
   */
  static generate30DayToken(): string {
    return tempAccessManager.generateTempUrl({
      expirationHours: 30 * 24, // 30 days
      maxUses: 100,
      allowedPaths: ['*']
    })
  }
}

// Create singleton instance
export const passwordManager = new PasswordManager()

// Console helpers for admin use (token generators only - no password exposure)
if (typeof window !== 'undefined') {
  // Only expose token generators, not password management functions
  ;(window as any).generate14DayToken = TokenGenerator.generate14DayToken
  ;(window as any).generate21DayToken = TokenGenerator.generate21DayToken
  ;(window as any).generate30DayToken = TokenGenerator.generate30DayToken
}
